import confetti from 'canvas-confetti';
import { localize } from '../utils/Localization';
import * as AudioManager from '../utils/AudioManager';
import { RANKS, levelProgress, nextRank, rankForLevel, showRankIcon, xpToNextLevel } from '../utils/Ranks';
import '../css/XPToast.scss';

let element = null;
let run = 0;

const part = name => element.querySelector(`.xp-toast-${name}`);
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

const create = () => {
	element = document.createElement('div');
	element.className = 'xp-toast';
	element.hidden = true;
	element.innerHTML = /*html*/`
		<div class="xp-toast-banner"></div>
		<div class="xp-toast-emblem">
			<div class="xp-toast-icon"></div>
			<div class="xp-toast-level"></div>
		</div>
		<div class="xp-toast-body">
			<div class="xp-toast-top">
				<span class="xp-toast-name"></span>
				<span class="xp-toast-gain"></span>
			</div>
			<div class="xp-toast-bar"><div class="xp-toast-fill"></div></div>
			<div class="xp-toast-bottom">
				<span class="xp-toast-xp"></span>
				<span class="xp-toast-next"><span class="xp-toast-next-icon"></span><span class="xp-toast-next-level"></span></span>
			</div>
		</div>
		<div class="xp-toast-discord" hidden>
			<span class="xp-toast-discord-icon" aria-hidden="true"></span>
			<span class="xp-toast-discord-text"></span>
			<button type="button" class="xp-toast-discord-join"></button>
		</div>
	`;
	document.getElementById('game-ui-container').appendChild(element);
};

const showDiscordJoin = (level, newRank, join) => {
	const rank = rankForLevel(level);
	part('discord-text').textContent = newRank && rank ? localize('discord_toast_rank', {rank: rank.name}) : localize('discord_toast_level');
	const button = part('discord-join');
	button.textContent = localize(newRank && rank ? 'discord_claim' : 'discord_join_short');
	button.onclick = () => {
		part('discord').hidden = true;
		join();
	};
	part('discord').hidden = false;
};

const setLevel = level => {
	const rank = rankForLevel(level);
	element.classList.toggle('unranked', !rank);
	showRankIcon(part('icon'), rank || RANKS[0]);
	part('name').textContent = rank ? rank.name : localize('rank_unranked');
	part('level').textContent = level;

	const next = nextRank(level);
	part('next-icon').hidden = !next;
	if(next) showRankIcon(part('next-icon'), next);
	part('next-level').textContent = next ? localize('rank_level', {level: next.level}) : localize('rank_max');
};

const setXP = (xp, needed) => {
	part('fill').style.width = `${xp / needed * 100}%`;
	part('xp').textContent = `${Math.round(xp).toLocaleString()} / ${needed.toLocaleString()} XP`;
};

const fillTo = (from, to, needed, token) => new Promise(resolve => {
	const duration = reducedMotion() ? 0 : 250 + 900 * (to - from) / needed;
	const start = performance.now();
	const step = now => {
		if(token !== run) return resolve();
		const t = duration ? Math.min(1, (now - start) / duration) : 1;
		const eased = 1 - Math.pow(1 - t, 3);
		setXP(from + (to - from) * eased, needed);
		if(t < 1) requestAnimationFrame(step);
		else resolve();
	};
	requestAnimationFrame(step);
});

const replay = (target, className, ...clear) => {
	target.classList.remove(className, ...clear);
	void target.offsetWidth;
	target.classList.add(className);
};

const celebrate = newRank => {
	const box = part('emblem').getBoundingClientRect();
	confetti({
		particleCount: newRank ? 90 : 35,
		spread: newRank ? 100 : 70,
		startVelocity: newRank ? 38 : 26,
		ticks: 90,
		origin: {x: (box.left + box.width / 2) / innerWidth, y: (box.top + box.height / 2) / innerHeight},
		colors: ['#D70000', '#FFD409', '#51a327', '#1F9891', '#ffffff'],
		zIndex: 30,
		disableForReducedMotion: true,
	});
};

const levelUp = (level, newRank) => {
	setLevel(level);
	part('banner').textContent = localize(newRank ? 'rank_newrank' : 'rank_levelup');
	replay(part('banner'), 'pop');
	replay(element, newRank ? 'rank-up' : 'level-up', 'level-up', 'rank-up');
	celebrate(newRank);
	if(newRank) AudioManager.playSFX('confetti', 0.6, 1.0);
	else AudioManager.playSFX('checkpoint', 0.4, 1.3);
};

export const showXPGain = async (gained, standing, join) => {
	if(!gained || !standing) return;
	if(!element) create();
	const token = ++run;

	const before = levelProgress(Math.max(0, standing.xp - gained));
	let level = before.level;
	let xp = before.xp;
	let needed = before.needed;
	let rankedUp = false;

	element.classList.remove('level-up', 'rank-up', 'hide');
	part('banner').classList.remove('pop');
	part('discord').hidden = true;
	part('gain').textContent = `+${gained.toLocaleString()} XP`;
	setLevel(level);
	setXP(xp, needed);
	element.hidden = false;
	replay(element, 'show');

	await wait(reducedMotion() ? 0 : 500);
	while(token === run && level < standing.level){
		await fillTo(xp, needed, needed, token);
		if(token !== run) return;
		level++;
		const newRank = RANKS.some(rank => rank.level === level);
		rankedUp = rankedUp || newRank;
		levelUp(level, newRank);
		if(join) showDiscordJoin(level, rankedUp, join);
		xp = 0;
		needed = xpToNextLevel(level);
		setXP(xp, needed);
		await wait(reducedMotion() ? 0 : newRank ? 1100 : 700);
	}
	if(token !== run) return;
	await fillTo(xp, standing.level_xp, standing.level_xp_needed, token);

	await wait(!part('discord').hidden ? 6000 : rankedUp ? 5000 : level > before.level ? 4000 : 3000);
	if(token !== run) return;
	element.classList.add('hide');
	await wait(400);
	if(token === run) element.hidden = true;
};
